import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../firebase";

// Re-export increment for use in other components
export { increment };

// ==================== CATEGORIES ====================

/**
 * Create a new category with subcategory support
 */
export const createCategory = async (categoryData) => {
  try {
    // Build path array for hierarchy
    let path = [];
    let level = 0;

    if (categoryData.parentCategory) {
      // Get parent to build path
      const parentDoc = await getDoc(doc(db, "categories", categoryData.parentCategory));
      if (parentDoc.exists()) {
        const parentData = parentDoc.data();
        path = [...(parentData.path || []), categoryData.parentCategory];
        level = (parentData.level || 0) + 1;
      }
    }

    const docRef = await addDoc(collection(db, "categories"), {
      ...categoryData,
      parentCategory: categoryData.parentCategory || null,
      level,
      path,
      order: categoryData.order || 0,
      stats: {
        quizCount: 0,
        totalViews: 0,
        totalAttempts: 0,
        ...(categoryData.stats || {})
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error creating category:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Default categories to create for new users
 */
const DEFAULT_CATEGORIES = [
  {
    name: 'Technology',
    slug: 'technology',
    description: 'Explore the world of technology, computers, and programming',
    coverImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=500',
    icon: '💻'
  },
  {
    name: 'Medicine',
    slug: 'medicine',
    description: 'Explore the fascinating world of medicine and healthcare',
    coverImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=500',
    icon: '🏥'
  },
  {
    name: 'Agriculture',
    slug: 'agriculture',
    description: 'Learn about farming, crop cultivation, and agricultural practices',
    coverImage: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=500',
    icon: '🌾'
  },
  {
    name: 'History',
    slug: 'history',
    description: 'Journey through time and explore historical events',
    coverImage: 'https://images.unsplash.com/photo-1461360370896-922624d12aa1?w=500',
    icon: '📜'
  },
  {
    name: 'Art',
    slug: 'art',
    description: 'Visual arts, music, and creative expression',
    coverImage: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=500',
    icon: '🎨'
  },
  {
    name: 'Sport',
    slug: 'sport',
    description: 'Sports, athletics, and physical activities',
    coverImage: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=500',
    icon: '⚽'
  },
  {
    name: 'Mythology',
    slug: 'mythology',
    description: 'Ancient myths, legends, and folklore',
    coverImage: 'https://images.unsplash.com/photo-1532153955177-f59af40d6472?w=500',
    icon: '🏛️'
  }
];

/**
 * Initialize default categories for a user
 */
export const initializeDefaultCategories = async (userId) => {
  try {
    const createdCategories = [];

    for (const category of DEFAULT_CATEGORIES) {
      const result = await addDoc(collection(db, "categories"), {
        ...category,
        createdBy: userId,
        isPublic: true,
        isSystemCategory: false, // These are user's personal default categories
        parentCategory: null, // Top-level categories
        level: 0,
        path: [],
        order: 0,
        stats: {
          quizCount: 0,
          totalViews: 0,
          totalAttempts: 0
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      createdCategories.push({ id: result.id, ...category });
    }

    return { success: true, data: createdCategories };
  } catch (error) {
    console.error("Error initializing default categories:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all categories (with default initialization for new users)
 */
export const getCategories = async (userId = null) => {
  try {
    let categories = [];

    if (userId) {
      // Get ALL user's categories (both created by them directly and default ones)
      const userQuery = query(
        collection(db, "categories"),
        where("createdBy", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);
      userSnapshot.forEach((doc) => {
        categories.push({ id: doc.id, ...doc.data() });
      });

      // If user has no categories, initialize defaults
      if (categories.length === 0) {
        console.log('No categories found, initializing defaults for user:', userId);
        const initResult = await initializeDefaultCategories(userId);
        if (initResult.success) {
          return { success: true, data: initResult.data };
        }
      }
    } else {
      // No userId provided - return all categories (admin view)
      const q = collection(db, "categories");
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        categories.push({ id: doc.id, ...doc.data() });
      });
    }

    // Sort categories: top-level first (no parent), then alphabetically
    categories.sort((a, b) => {
      // Group by level (top-level categories first)
      const levelA = a.level || 0;
      const levelB = b.level || 0;
      if (levelA !== levelB) return levelA - levelB;

      // Then alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });

    return { success: true, data: categories };
  } catch (error) {
    console.error("Error getting categories:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single category by ID
 */
export const getCategoryById = async (categoryId) => {
  try {
    const docRef = doc(db, "categories", categoryId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: "Category not found" };
    }
  } catch (error) {
    console.error("Error getting category:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single category by slug
 */
export const getCategoryBySlug = async (slug) => {
  try {
    const categoriesRef = collection(db, "categories");
    const q = query(categoriesRef, where("slug", "==", slug), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { success: true, data: { id: doc.id, ...doc.data() } };
    } else {
      return { success: false, error: "Category not found" };
    }
  } catch (error) {
    console.error("Error getting category by slug:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a category
 */
export const updateCategory = async (categoryId, updates) => {
  try {
    const docRef = doc(db, "categories", categoryId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating category:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a category
 */
export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, "categories", categoryId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting category:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get subcategories of a category
 */
export const getSubcategories = async (parentCategoryId) => {
  try {
    const q = query(
      collection(db, "categories"),
      where("parentCategory", "==", parentCategoryId)
    );

    const querySnapshot = await getDocs(q);
    const subcategories = [];
    querySnapshot.forEach((doc) => {
      subcategories.push({ id: doc.id, ...doc.data() });
    });

    // Sort by order in memory (to avoid needing a composite index)
    subcategories.sort((a, b) => (a.order || 0) - (b.order || 0));

    return { success: true, data: subcategories };
  } catch (error) {
    console.error("Error getting subcategories:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get category breadcrumb path (ancestors)
 */
export const getCategoryPath = async (categoryId) => {
  try {
    const categoryDoc = await getDoc(doc(db, "categories", categoryId));
    if (!categoryDoc.exists()) {
      return { success: false, error: "Category not found" };
    }

    const categoryData = categoryDoc.data();
    const path = categoryData.path || [];

    // Fetch all ancestor categories
    const ancestors = await Promise.all(
      path.map(async (ancestorId) => {
        const ancestorDoc = await getDoc(doc(db, "categories", ancestorId));
        return ancestorDoc.exists()
          ? { id: ancestorDoc.id, ...ancestorDoc.data() }
          : null;
      })
    );

    // Add current category at the end
    const fullPath = [
      ...ancestors.filter(Boolean),
      { id: categoryDoc.id, ...categoryData }
    ];

    return { success: true, data: fullPath };
  } catch (error) {
    console.error("Error getting category path:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all root categories (no parent)
 */
export const getRootCategories = async () => {
  try {
    const q = query(
      collection(db, "categories"),
      where("parentCategory", "==", null)
    );

    const querySnapshot = await getDocs(q);
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });

    // Sort by order in memory (to avoid needing a composite index)
    categories.sort((a, b) => (a.order || 0) - (b.order || 0));

    return { success: true, data: categories };
  } catch (error) {
    console.error("Error getting root categories:", error);
    return { success: false, error: error.message };
  }
};

// ==================== QUIZZES ====================

/**
 * Check if a quiz title already exists in a category
 */
export const checkQuizTitleExists = async (title, categoryId) => {
  try {
    const q = query(
      collection(db, "quizzes"),
      where("title", "==", title),
      where("category", "==", categoryId)
    );
    const querySnapshot = await getDocs(q);
    return { success: true, exists: !querySnapshot.empty };
  } catch (error) {
    console.error("Error checking quiz title:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new quiz
 */
export const createQuiz = async (quizData) => {
  try {
    // Check if quiz title already exists in this category
    if (quizData.title && quizData.category) {
      const titleCheck = await checkQuizTitleExists(quizData.title, quizData.category);
      if (!titleCheck.success) {
        return { success: false, error: "Failed to validate quiz title" };
      }
      if (titleCheck.exists) {
        return { success: false, error: "A quiz with this title already exists in this category" };
      }
    }

    // Generate slug from title
    const slug = quizData.title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen

    const docRef = await addDoc(collection(db, "quizzes"), {
      ...quizData,
      slug,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      stats: {
        views: 0,
        attempts: 0,
        totalScore: 0,
        averageScore: 0,
      },
    });
    return { success: true, id: docRef.id, slug };
  } catch (error) {
    console.error("Error creating quiz:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get quizzes by category
 */
export const getQuizzesByCategory = async (categoryId, userId = null) => {
  try {
    let q;
    if (userId) {
      // Get public quizzes OR user's own quizzes in this category
      q = query(
        collection(db, "quizzes"),
        where("category", "==", categoryId),
        orderBy("createdAt", "desc")
      );
    } else {
      // Get only public quizzes
      q = query(
        collection(db, "quizzes"),
        where("category", "==", categoryId),
        where("isPublic", "==", true),
        orderBy("createdAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const quizzes = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter to show only public quizzes or user's own quizzes
      if (data.isPublic || (userId && data.createdBy === userId)) {
        quizzes.push({ id: doc.id, ...data });
      }
    });

    return { success: true, data: quizzes };
  } catch (error) {
    console.error("Error getting quizzes:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single quiz by ID
 */
export const getQuizById = async (quizId) => {
  try {
    const docRef = doc(db, "quizzes", quizId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Increment view count
      await updateDoc(docRef, {
        "stats.views": increment(1),
      });

      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: "Quiz not found" };
    }
  } catch (error) {
    console.error("Error getting quiz:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single quiz by slug
 */
export const getQuizBySlug = async (quizSlug) => {
  try {
    const q = query(collection(db, "quizzes"), where("slug", "==", quizSlug), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const docRef = doc(db, "quizzes", docSnap.id);
      const quizData = docSnap.data();

      // Increment view count
      await updateDoc(docRef, {
        "stats.views": increment(1),
      });

      // Fetch category info if quiz has a category
      let categoryInfo = {};
      if (quizData.category) {
        try {
          const categoryDoc = await getDoc(doc(db, "categories", quizData.category));
          if (categoryDoc.exists()) {
            const catData = categoryDoc.data();
            categoryInfo = {
              categoryId: quizData.category,
              categoryName: catData.name,
              categorySlug: catData.slug
            };
          }
        } catch (err) {
          console.error("Error fetching category info:", err);
        }
      }

      return {
        success: true,
        data: {
          id: docSnap.id,
          ...quizData,
          ...categoryInfo
        }
      };
    } else {
      return { success: false, error: "Quiz not found" };
    }
  } catch (error) {
    console.error("Error getting quiz by slug:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get quizzes created by a specific user
 */
export const getQuizzesByUser = async (userId) => {
  try {
    const q = query(
      collection(db, "quizzes"),
      where("createdBy", "==", userId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const quizzes = [];
    querySnapshot.forEach((doc) => {
      quizzes.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: quizzes };
  } catch (error) {
    console.error("Error getting user quizzes:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a quiz
 */
export const updateQuiz = async (quizId, updates) => {
  try {
    const docRef = doc(db, "quizzes", quizId);

    // If category is being changed, check for duplicate title in new category
    if (updates.category && updates.title) {
      // Get current quiz data to compare category change
      const currentDoc = await getDoc(docRef);
      if (currentDoc.exists()) {
        const currentData = currentDoc.data();

        // Only check if category is actually changing
        if (currentData.category !== updates.category) {
          const titleCheck = await checkQuizTitleExists(updates.title, updates.category);

          if (!titleCheck.success) {
            return {
              success: false,
              error: "Failed to validate quiz title in the new category"
            };
          }

          if (titleCheck.exists) {
            return {
              success: false,
              error: `A quiz titled "${updates.title}" already exists in this category. Please choose a different category or rename the quiz.`,
              isDuplicate: true
            };
          }
        }
      }
    }

    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    return { success: true, data: { id: quizId } };
  } catch (error) {
    console.error("Error updating quiz:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a quiz
 */
export const deleteQuiz = async (quizId) => {
  try {
    await deleteDoc(doc(db, "quizzes", quizId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting quiz:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update quiz statistics after completion
 */
export const updateQuizStats = async (quizId, score) => {
  try {
    const docRef = doc(db, "quizzes", quizId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const currentStats = docSnap.data().stats;
      const newAttempts = currentStats.attempts + 1;
      const newTotalScore = currentStats.totalScore + score;
      const newAverageScore = newTotalScore / newAttempts;

      await updateDoc(docRef, {
        "stats.attempts": increment(1),
        "stats.totalScore": increment(score),
        "stats.averageScore": newAverageScore,
      });

      return { success: true };
    } else {
      return { success: false, error: "Quiz not found" };
    }
  } catch (error) {
    console.error("Error updating quiz stats:", error);
    return { success: false, error: error.message };
  }
};

// ==================== USER QUIZ ATTEMPTS ====================

/**
 * Create a quiz attempt (alias for saveQuizAttempt for consistency)
 */
export const createQuizAttempt = async (attemptData) => {
  try {
    const docRef = await addDoc(collection(db, "quizAttempts"), {
      ...attemptData,
      completedAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error creating quiz attempt:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Save a quiz attempt (legacy alias)
 */
export const saveQuizAttempt = createQuizAttempt;

/**
 * Get a single quiz attempt by ID
 */
export const getQuizAttemptById = async (attemptId) => {
  try {
    const docRef = doc(db, "quizAttempts", attemptId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: "Quiz attempt not found" };
    }
  } catch (error) {
    console.error("Error getting quiz attempt:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's quiz attempts
 */
export const getUserQuizAttempts = async (userId, quizId = null) => {
  try {
    let q;
    if (quizId) {
      // Get attempts for a specific quiz
      q = query(
        collection(db, "quizAttempts"),
        where("userId", "==", userId),
        where("quizId", "==", quizId),
        orderBy("completedAt", "desc")
      );
    } else {
      // Get all attempts by user
      q = query(
        collection(db, "quizAttempts"),
        where("userId", "==", userId),
        orderBy("completedAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const attempts = [];
    querySnapshot.forEach((doc) => {
      attempts.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: attempts };
  } catch (error) {
    console.error("Error getting quiz attempts:", error);
    return { success: false, error: error.message };
  }
};

// ==================== LEADERBOARD ====================

/**
 * Get leaderboard for a specific quiz
 */
export const getQuizLeaderboard = async (quizId, limitCount = 10) => {
  try {
    const q = query(
      collection(db, "quizAttempts"),
      where("quizId", "==", quizId),
      orderBy("score", "desc"),
      orderBy("timeSpent", "asc"), // If scores are equal, faster time wins
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const leaderboard = [];
    querySnapshot.forEach((doc) => {
      leaderboard.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: leaderboard };
  } catch (error) {
    console.error("Error getting leaderboard:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get global leaderboard (top performers across all quizzes)
 */
export const getGlobalLeaderboard = async (limitCount = 10) => {
  try {
    const q = query(
      collection(db, "quizAttempts"),
      orderBy("score", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const leaderboard = [];
    querySnapshot.forEach((doc) => {
      leaderboard.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: leaderboard };
  } catch (error) {
    console.error("Error getting global leaderboard:", error);
    return { success: false, error: error.message };
  }
};

// ==================== USER PROFILE ====================

/**
 * Create or update user profile
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const docRef = doc(db, "users", userId);
    await updateDoc(docRef, {
      ...profileData,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    // If document doesn't exist, create it
    try {
      await addDoc(collection(db, "users"), {
        userId,
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (createError) {
      console.error("Error creating user profile:", createError);
      return { success: false, error: createError.message };
    }
  }
};

/**
 * Get user profile
 */
export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, "users", userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: "User profile not found" };
    }
  } catch (error) {
    console.error("Error getting user profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user stats (streak, quizzes completed, total points)
 */
export const getUserStats = async (userId) => {
  try {
    // Get user attempts
    const attemptsQuery = query(
      collection(db, "quizAttempts"),
      where("userId", "==", userId),
      orderBy("completedAt", "desc")
    );

    const attemptsSnapshot = await getDocs(attemptsQuery);
    const attempts = [];
    attemptsSnapshot.forEach((doc) => {
      attempts.push({ id: doc.id, ...doc.data() });
    });

    // Calculate total points
    const totalPoints = attempts.reduce((sum, attempt) => sum + (attempt.score || 0), 0);

    // Calculate streak (consecutive days)
    let streak = 0;
    if (attempts.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const attemptDates = attempts.map(attempt => {
        const date = attempt.completedAt?.toDate() || new Date(attempt.completedAt);
        date.setHours(0, 0, 0, 0);
        return date.getTime();
      });

      // Get unique dates (remove duplicates from same day)
      const uniqueDates = [...new Set(attemptDates)].sort((a, b) => b - a);

      // Check if streak is still active (today or yesterday)
      const dayInMs = 24 * 60 * 60 * 1000;
      const todayMs = today.getTime();
      const yesterdayMs = todayMs - dayInMs;

      if (uniqueDates[0] === todayMs || uniqueDates[0] === yesterdayMs) {
        streak = 1;
        let currentDate = uniqueDates[0];

        for (let i = 1; i < uniqueDates.length; i++) {
          const expectedPrevDay = currentDate - dayInMs;
          if (uniqueDates[i] === expectedPrevDay) {
            streak++;
            currentDate = uniqueDates[i];
          } else {
            break;
          }
        }
      }
    }

    return {
      success: true,
      data: {
        streak,
        quizzesCompleted: attempts.length,
        totalPoints,
      },
    };
  } catch (error) {
    console.error("Error getting user stats:", error);
    return {
      success: false,
      error: error.message,
      data: { streak: 0, quizzesCompleted: 0, totalPoints: 0 }
    };
  }
};

/**
 * Check if username is available (unique)
 */
export const checkUsernameAvailability = async (username) => {
  try {
    const q = query(
      collection(db, "users"),
      where("username", "==", username.toLowerCase())
    );
    const querySnapshot = await getDocs(q);
    return { success: true, available: querySnapshot.empty };
  } catch (error) {
    console.error("Error checking username:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create user profile with username (for new signups)
 */
export const createUserProfile = async (userId, { username, email, displayName = null }) => {
  try {
    // Check if username is available
    const usernameCheck = await checkUsernameAvailability(username);
    if (!usernameCheck.success) {
      return { success: false, error: "Failed to validate username" };
    }
    if (!usernameCheck.available) {
      return { success: false, error: "Username already taken" };
    }

    // Create user document with userId as document ID
    await updateDoc(doc(db, "users", userId), {
      userId,
      username: username.toLowerCase(),
      displayName: displayName || username,
      email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    // If document doesn't exist, use setDoc instead
    try {
      const { setDoc } = await import("firebase/firestore");
      await setDoc(doc(db, "users", userId), {
        userId,
        username: username.toLowerCase(),
        displayName: displayName || username,
        email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (createError) {
      console.error("Error creating user profile:", createError);
      return { success: false, error: createError.message };
    }
  }
};

/**
 * Get user by username (for login with username)
 */
export const getUserByUsername = async (username) => {
  try {
    const q = query(
      collection(db, "users"),
      where("username", "==", username.toLowerCase())
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: false, error: "User not found" };
    }

    const userDoc = querySnapshot.docs[0];
    return {
      success: true,
      data: { id: userDoc.id, ...userDoc.data() }
    };
  } catch (error) {
    console.error("Error getting user by username:", error);
    return { success: false, error: error.message };
  }
};

// ==================== SEARCH ====================

/**
 * Search quizzes by title
 */
export const searchQuizzes = async (searchTerm) => {
  try {
    // Note: Firestore doesn't support full-text search natively
    // This is a simple implementation. For production, consider using Algolia or similar
    const q = query(
      collection(db, "quizzes"),
      where("isPublic", "==", true),
      orderBy("title")
    );

    const querySnapshot = await getDocs(q);
    const quizzes = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter by search term (case-insensitive)
      if (
        data.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.description.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        quizzes.push({ id: doc.id, ...data });
      }
    });

    return { success: true, data: quizzes };
  } catch (error) {
    console.error("Error searching quizzes:", error);
    return { success: false, error: error.message };
  }
};

// ==================== NOTIFICATIONS ====================

/**
 * Get all notifications for a user
 */
export const getUserNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("isArchived", "==", false),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const notifications = [];

    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, notifications };
  } catch (error) {
    console.error("Error getting notifications:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get notifications with pagination
 */
export const getUserNotificationsPaginated = async (userId, lastDoc = null, limitCount = 20) => {
  try {
    let q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("isArchived", "==", false),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );

    if (lastDoc) {
      const { startAfter: startAfterFn } = await import("firebase/firestore");
      q = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        where("isArchived", "==", false),
        orderBy("createdAt", "desc"),
        startAfterFn(lastDoc),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    const notifications = [];

    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });

    const hasMore = querySnapshot.docs.length === limitCount;
    const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      success: true,
      data: notifications,
      hasMore,
      lastDoc: newLastDoc,
    };
  } catch (error) {
    console.error("Error getting paginated notifications:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark a single notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, "notifications", notificationId), {
      read: true,
      readAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);

    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("read", "==", false)
    );

    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((notifDoc) => {
      batch.update(notifDoc.ref, {
        read: true,
        readAt: serverTimestamp(),
      });
    });

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Archive a single notification
 */
export const archiveNotification = async (notificationId) => {
  try {
    await updateDoc(doc(db, "notifications", notificationId), {
      isArchived: true,
      archivedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error archiving notification:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Archive all notifications for a user
 */
export const archiveAllNotifications = async (userId) => {
  try {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);

    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("isArchived", "==", false)
    );

    const querySnapshot = await getDocs(q);
    const archivedIds = [];

    querySnapshot.forEach((notifDoc) => {
      batch.update(notifDoc.ref, {
        isArchived: true,
        archivedAt: serverTimestamp(),
      });
      archivedIds.push(notifDoc.id);
    });

    await batch.commit();

    return {
      success: true,
      count: archivedIds.length,
      archivedIds,
    };
  } catch (error) {
    console.error("Error archiving all notifications:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Unarchive notifications by IDs
 */
export const unarchiveNotifications = async (notificationIds) => {
  try {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);

    notificationIds.forEach((notifId) => {
      batch.update(doc(db, "notifications", notifId), {
        isArchived: false,
        archivedAt: null,
      });
    });

    await batch.commit();

    return {
      success: true,
      count: notificationIds.length,
    };
  } catch (error) {
    console.error("Error unarchiving notifications:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new notification
 */
export const createNotification = async (notificationData) => {
  try {
    const docRef = await addDoc(collection(db, "notifications"), {
      ...notificationData,
      read: false,
      isArchived: false,
      createdAt: serverTimestamp(),
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error creating notification:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (userId) => {
  try {
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("read", "==", false),
      where("isArchived", "==", false)
    );

    const querySnapshot = await getDocs(q);

    return {
      success: true,
      count: querySnapshot.size,
    };
  } catch (error) {
    console.error("Error getting unread count:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, "notifications", notificationId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting notification:", error);
    return { success: false, error: error.message };
  }
};
